import { useState, useEffect, useCallback } from "react";

const KAFKA_SERVERS = {
  dev:  { label:"TN-Dev",         url:"/kafka-dev-api",  accent:"#06b6d4" },
  ps2:  { label:"TN-PS-MQTT/TCP", url:"/kafka-ps2-api",  accent:"#8b5cf6" },
  fsa:  { label:"FSA",            url:"/fsa-kafka-api",  accent:"#f59e0b" },
};

export default function KafkaPanel({ token, theme, env }) {
  const visibleServers = Object.fromEntries(
    Object.entries(KAFKA_SERVERS).filter(([k]) =>
      (env||"tnpdcl")==="fsa" ? k==="fsa" : k!=="fsa"
    )
  );
  const [server, setServer]   = useState((env||"tnpdcl")==="fsa"?"fsa":"dev");
  const [data,   setData]     = useState(null);
  const [loading,setLoading]  = useState(true);
  const [search, setSearch]   = useState("");
  const [tab,    setTab]      = useState("overview");
  const [selTopic,   setSelTopic]   = useState(null);
  const [topicDetail,setTopicDetail]= useState(null);
  const [topicLoading,setTopicLoading]= useState(false);
  const [showConns, setShowConns] = useState(false);

  const H = { "X-Auth-Token": token };
  const cfg = KAFKA_SERVERS[server] || Object.values(visibleServers)[0];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${cfg.url}/kafka`, { headers: H });
      setData(await r.json());
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [server, token]);

  useEffect(() => { load(); const id = setInterval(load, 60000); return ()=>clearInterval(id); }, [load]);

  const loadTopic = async (name) => {
    setSelTopic(name); setTopicLoading(true); setTopicDetail(null);
    try {
      const r = await fetch(`${cfg.url}/kafka/topic/${name}`, { headers: H });
      setTopicDetail(await r.json());
    } catch(e) { console.error(e); }
    setTopicLoading(false);
  };

  const isDark = ["midnight","slate","cyberpunk","volcano","forest","ocean","aurora","tech"].includes(theme?.bg);

  const card = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 12, padding: "16px 20px",
  };

  const statCard = (label, value, color, sub) => (
    <div style={{...card, textAlign:"center"}}>
      <div style={{fontSize:11,fontWeight:700,color:theme.textMuted,letterSpacing:".1em",
        fontFamily:"'Inter',sans-serif",marginBottom:8}}>{label}</div>
      <div style={{fontSize:28,fontWeight:800,color:color,fontFamily:"'Inter',sans-serif",
        letterSpacing:"-.02em"}}>{value}</div>
      {sub && <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'Inter',sans-serif",marginTop:4}}>{sub}</div>}
    </div>
  );

  return (
    <div style={{flex:1,display:"flex",flexDirection:"column",
      background:theme.bg,fontFamily:"'Inter',sans-serif",overflow:"hidden",minHeight:0}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{padding:"12px 20px",borderBottom:`1px solid ${theme.border}`,
        background:theme.bgCard,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <div style={{fontSize:16,fontWeight:800,color:theme.text,letterSpacing:"-.01em"}}>
          📨 Kafka Monitor
        </div>
        <div style={{width:1,height:20,background:theme.border}}/>
        {/* Server selector */}
        {Object.entries(visibleServers).map(([k,s])=>(
          <button key={k} onClick={()=>setServer(k)} style={{
            background:server===k?`${s.accent}18`:theme.inputBg,
            border:`1px solid ${server===k?`${s.accent}50`:theme.border}`,
            color:server===k?s.accent:theme.textMuted,
            borderRadius:6,padding:"5px 12px",cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
          }}>{s.label}</button>
        ))}
        <div style={{width:1,height:20,background:theme.border}}/>
        {/* Tabs */}
        {[["overview","📊 Overview"],["topics","📋 Topics"],["groups","👥 Groups"]].map(([k,l])=>(
          <button key={k} onClick={()=>setTab(k)} style={{
            background:tab===k?`${cfg.accent}18`:theme.inputBg,
            border:`1px solid ${tab===k?`${cfg.accent}50`:theme.border}`,
            color:tab===k?cfg.accent:theme.textMuted,
            borderRadius:6,padding:"5px 12px",cursor:"pointer",
            fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
          }}>{l}</button>
        ))}
        <div style={{flex:1}}/>
        <button onClick={load} style={{
          background:`${cfg.accent}18`,border:`1px solid ${cfg.accent}50`,
          color:cfg.accent,borderRadius:6,padding:"5px 12px",cursor:"pointer",
          fontFamily:"'Inter',sans-serif",fontSize:12,fontWeight:600,
        }}>↻ Refresh</button>
        {loading && <div style={{width:16,height:16,borderRadius:"50%",
          border:`2px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
          animation:"spin .7s linear infinite"}}/>}
      </div>

      {/* Content */}
      <div style={{flex:1,overflowY:"auto",padding:20,minHeight:0,paddingBottom:40}}>
        {!data && loading && (
          <div style={{display:"flex",alignItems:"center",justifyContent:"center",
            flexDirection:"column",gap:16,height:300}}>
            <div style={{width:48,height:48,borderRadius:"50%",
              border:`3px solid ${cfg.accent}30`,borderTopColor:cfg.accent,
              animation:"spin .8s linear infinite"}}/>
            <div style={{fontSize:13,color:theme.textMuted}}>Loading Kafka metrics...</div>
          </div>
        )}

        {data && tab==="overview" && (
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            {/* Stat cards */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:12}}>
              {statCard("BROKER STATUS",
                data.broker?.status==="running"?"●  ONLINE":"●  OFFLINE",
                data.broker?.status==="running"?"#10b981":"#ef4444",
                `PID: ${data.broker?.pid||"—"}`)}
              <div onClick={()=>setShowConns(v=>!v)} style={{cursor:"pointer"}}>
                {statCard("CONNECTIONS", data.broker?.connections||0, cfg.accent,
                  showConns?"▼ click to hide":"▲ click for details")}
              </div>
              {statCard("TOPICS", data.total_topics||0, "#f59e0b", "active topics")}
              {statCard("CONSUMER GROUPS", data.total_consumer_groups||0, "#a78bfa", "groups")}
              {statCard("TOTAL SIZE", data.total_size||"—", "#10b981", "all topic data")}
            </div>

            {/* Connection details */}
            {showConns && (
              <div style={card}>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:14}}>🔌 CONNECTION DETAILS</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
                  {/* By Process */}
                  {/* By Application */}
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:theme.textMuted,
                      marginBottom:12,letterSpacing:".1em"}}>⚙️ BY APPLICATION</div>
                    {(data.broker?.connections_by_process||[]).length===0 ? (
                      <div style={{fontSize:12,color:theme.textMuted,fontStyle:"italic"}}>
                        No active application connections
                      </div>
                    ) : (data.broker?.connections_by_process||[]).map((p,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",padding:"8px 10px",borderRadius:6,marginBottom:4,
                        background:i%2===0?theme.inputBg:"transparent"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:6,height:6,borderRadius:"50%",
                            background:cfg.accent,flexShrink:0}}/>
                          <span style={{fontSize:12,color:theme.text,
                            fontFamily:"'JetBrains Mono',monospace",
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",
                            maxWidth:180}}>{p.process}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                          <span style={{fontSize:11,color:theme.textMuted}}>connections</span>
                          <span style={{fontSize:13,fontWeight:700,color:cfg.accent,
                            fontFamily:"'JetBrains Mono',monospace",
                            background:`${cfg.accent}15`,padding:"2px 8px",
                            borderRadius:5}}>{p.connections}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* By IP */}
                  <div>
                    <div style={{fontSize:11,fontWeight:700,color:theme.textMuted,
                      marginBottom:12,letterSpacing:".1em"}}>🌐 BY IP ADDRESS</div>
                    {(data.broker?.connections_by_ip||[]).length===0 ? (
                      <div style={{fontSize:12,color:theme.textMuted,fontStyle:"italic"}}>
                        No external connections
                      </div>
                    ) : (data.broker?.connections_by_ip||[])
                      .filter(p=>!p.ip.startsWith("::ffff:"))
                      .map((p,i)=>(
                      <div key={i} style={{display:"flex",justifyContent:"space-between",
                        alignItems:"center",padding:"8px 10px",borderRadius:6,marginBottom:4,
                        background:i%2===0?theme.inputBg:"transparent"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:6,height:6,borderRadius:"50%",
                            background:"#10b981",flexShrink:0}}/>
                          <span style={{fontSize:12,color:theme.text,
                            fontFamily:"'JetBrains Mono',monospace"}}>{p.ip}</span>
                        </div>
                        <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
                          <span style={{fontSize:11,color:theme.textMuted}}>connections</span>
                          <span style={{fontSize:13,fontWeight:700,color:"#10b981",
                            fontFamily:"'JetBrains Mono',monospace",
                            background:"rgba(16,185,129,0.15)",padding:"2px 8px",
                            borderRadius:5}}>{p.count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Top topics */}
            <div style={card}>
              <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                letterSpacing:".1em",marginBottom:14}}>🔥 TOP TOPICS BY SIZE</div>
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {(data.top_topics||[]).slice(0,10).map((t,i)=>{
                  const pct = data.total_size_bytes>0
                    ? Math.round(t.size_bytes/data.total_size_bytes*100) : 0;
                  return (
                    <div key={t.name}
                      onClick={()=>loadTopic(t.name)}
                      style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",
                        padding:"4px 8px",borderRadius:6,
                        background:selTopic===t.name?`${cfg.accent}10`:"transparent",
                        transition:"background .15s"}}>
                      <div style={{fontSize:11,color:theme.textMuted,fontFamily:"'JetBrains Mono',monospace",
                        minWidth:20,textAlign:"right"}}>{i+1}</div>
                      <div style={{flex:1,fontSize:12,color:selTopic===t.name?cfg.accent:theme.text,
                        fontFamily:"'JetBrains Mono',monospace",fontWeight:selTopic===t.name?700:400,
                        overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{t.name}</div>
                      <div style={{width:120,background:theme.border,borderRadius:4,height:6,flexShrink:0}}>
                        <div style={{width:`${pct}%`,height:"100%",borderRadius:4,
                          background:cfg.accent,transition:"width .3s"}}/>
                      </div>
                      <div style={{fontSize:12,fontWeight:700,color:cfg.accent,
                        fontFamily:"'JetBrains Mono',monospace",minWidth:70,textAlign:"right"}}>{t.size}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Topic detail panel */}
            {selTopic && (
              <div style={card}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{fontSize:13,fontWeight:700,color:theme.text,
                      fontFamily:"'JetBrains Mono',monospace"}}>{selTopic}</div>
                    {topicDetail && (
                      <span style={{fontSize:11,color:cfg.accent,background:`${cfg.accent}15`,
                        padding:"2px 8px",borderRadius:4,fontWeight:600}}>
                        {topicDetail.info?.partition_count} partitions · {topicDetail.total_size}
                      </span>
                    )}
                  </div>
                  <button onClick={()=>{setSelTopic(null);setTopicDetail(null);}} style={{
                    background:"transparent",border:"none",color:theme.textMuted,
                    cursor:"pointer",fontSize:16}}>✕</button>
                </div>
                {topicLoading && <div style={{fontSize:12,color:theme.textMuted}}>Loading partitions...</div>}
                {topicDetail && (
                  <div style={{display:"flex",flexDirection:"column",gap:6}}>
                    {/* Info row */}
                    <div style={{display:"flex",gap:16,marginBottom:8,flexWrap:"wrap"}}>
                      {[
                        ["Partitions", topicDetail.info?.partition_count],
                        ["Replication", topicDetail.info?.replication_factor],
                        ["Total Size", topicDetail.total_size],
                        ["Topic ID", topicDetail.info?.topic_id?.substring(0,12)+"..."],
                      ].filter(([,v])=>v).map(([k,v])=>(
                        <div key={k} style={{background:theme.inputBg,borderRadius:6,
                          padding:"6px 12px",display:"flex",gap:8,alignItems:"center"}}>
                          <span style={{fontSize:11,color:theme.textMuted}}>{k}:</span>
                          <span style={{fontSize:12,fontWeight:700,color:theme.text,
                            fontFamily:"'JetBrains Mono',monospace"}}>{v}</span>
                        </div>
                      ))}
                    </div>
                    {/* Partition table */}
                    <div style={{display:"grid",
                      gridTemplateColumns:"60px 80px 80px 80px 1fr",
                      gap:0,fontSize:11,fontWeight:700,color:theme.textMuted,
                      padding:"6px 10px",letterSpacing:".06em"}}>
                      <span>PART</span>
                      <span>LEADER</span>
                      <span>REPLICAS</span>
                      <span>ISR</span>
                      <span>SIZE</span>
                    </div>
                    {topicDetail.partitions.map((p,i)=>(
                      <div key={i} style={{display:"grid",
                        gridTemplateColumns:"60px 80px 80px 80px 1fr",
                        gap:0,padding:"7px 10px",borderRadius:6,
                        background:i%2===0?theme.inputBg:"transparent",
                        fontSize:12,fontFamily:"'JetBrains Mono',monospace"}}>
                        <span style={{color:cfg.accent,fontWeight:700}}>#{p.partition}</span>
                        <span style={{color:theme.text}}>{p.leader}</span>
                        <span style={{color:theme.text}}>{p.replicas}</span>
                        <span style={{color:"#10b981"}}>{p.isr}</span>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{flex:1,background:theme.border,borderRadius:3,height:4,maxWidth:150}}>
                            <div style={{
                              width:`${Math.min(p.size_bytes/Math.max(...topicDetail.partitions.map(x=>x.size_bytes||1))*100,100)}%`,
                              height:"100%",borderRadius:3,background:cfg.accent}}/>
                          </div>
                          <span style={{color:cfg.accent,fontWeight:700,minWidth:70}}>{p.size}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Broker info */}
            <div style={{...card,display:"grid",gridTemplateColumns:"1fr 1fr",gap:16}}>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:10}}>🖥 BROKER INFO</div>
                {[
                  ["Host", data.broker?.host||"localhost"],
                  ["Port", data.broker?.port||9092],
                  ["Status", data.broker?.status||"—"],
                  ["Connections", data.broker?.connections||0],
                  ["Last Updated", new Date(data.timestamp).toLocaleTimeString()],
                ].map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",
                    padding:"5px 0",borderBottom:`1px solid ${theme.border}`}}>
                    <span style={{fontSize:12,color:theme.textMuted}}>{k}</span>
                    <span style={{fontSize:12,fontWeight:600,color:theme.text,
                      fontFamily:"'JetBrains Mono',monospace"}}>{v}</span>
                  </div>
                ))}
              </div>
              <div>
                <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                  letterSpacing:".1em",marginBottom:10}}>📊 SUMMARY</div>
                {[
                  ["Total Topics", data.total_topics],
                  ["Consumer Groups", data.total_consumer_groups],
                  ["Total Data Size", data.total_size],
                  ["Kafka Home", "/mnt/apps/kafka"],
                  ["ZooKeeper", "localhost:2181"],
                ].map(([k,v])=>(
                  <div key={k} style={{display:"flex",justifyContent:"space-between",
                    padding:"5px 0",borderBottom:`1px solid ${theme.border}`}}>
                    <span style={{fontSize:12,color:theme.textMuted}}>{k}</span>
                    <span style={{fontSize:12,fontWeight:600,color:theme.text,
                      fontFamily:"'JetBrains Mono',monospace"}}>{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {data && tab==="topics" && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="🔍 Search topics..."
              style={{background:theme.inputBg,border:`1px solid ${theme.border}`,
                color:theme.text,borderRadius:8,padding:"8px 14px",
                fontFamily:"'Inter',sans-serif",fontSize:13,outline:"none",width:"100%",
                boxSizing:"border-box"}}/>
            <div style={card}>
              <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
                letterSpacing:".1em",marginBottom:12}}>
                📋 ALL TOPICS ({data.all_topics?.length||0})
              </div>
              <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                {(data.all_topics||[])
                  .filter(t=>!search||t.toLowerCase().includes(search.toLowerCase()))
                  .map(t=>(
                  <div key={t}
                    onClick={()=>{setTab("overview");loadTopic(t);}}
                    style={{
                      background:selTopic===t?`${cfg.accent}15`:theme.inputBg,
                      border:`1px solid ${selTopic===t?cfg.accent:theme.border}`,
                      borderRadius:6,padding:"4px 10px",fontSize:11,cursor:"pointer",
                      color:selTopic===t?cfg.accent:theme.text,
                      fontFamily:"'JetBrains Mono',monospace",
                    }}>{t}</div>
                ))}
              </div>
            </div>
          </div>
        )}

        {data && tab==="groups" && (
          <div style={card}>
            <div style={{fontSize:12,fontWeight:700,color:theme.textMuted,
              letterSpacing:".1em",marginBottom:12}}>
              👥 CONSUMER GROUPS ({data.consumer_groups?.length||0})
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:4}}>
              {(data.consumer_groups||[]).map((g,i)=>(
                <div key={g} style={{display:"flex",alignItems:"center",gap:10,
                  padding:"7px 10px",borderRadius:6,
                  background:i%2===0?theme.inputBg:"transparent"}}>
                  <div style={{width:6,height:6,borderRadius:"50%",
                    background:cfg.accent,flexShrink:0}}/>
                  <span style={{fontSize:12,color:theme.text,
                    fontFamily:"'JetBrains Mono',monospace"}}>{g}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
